import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../components/ui/accordion';
import { getLabels } from '../../../records/api/recordsApi';
import DeviceStickerButton from './DeviceStickerButton';
import DurationSlider from './DurationSlider';

function FilterDialog({
    open,
    onClose,
    onApply,
    onReset,
    startPicker,
    endPicker,
    statusFilter,
    onStatusChange,
    deviceTypeFilter,
    onDeviceTypeChange,
    durationRange,
    onDurationChange,
    labelFilter,
    onLabelChange,
    tKey,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: tKey });
    const filter = t('filter');
    const uploadTime = t('uploadTime');
    const status = t('status');
    const anyStatus = t('anyStatus');
    const pass = t('pass');
    const fail = t('fail');
    const deviceType = t('deviceType');
    const allDevice = t('allDevice');
    const label = t('label');
    const duration = t('duration');
    const minimumDays = t('minimumDays');
    const maximumDays = t('maximumDays');
    const noLimit = t('noLimit');
    const resetFilter = t('resetFilter');
    const apply = t('apply');

    const CloseIcon = () => (
        <svg className='h-5 w-5' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
                d='M15 5L5 15M5 5L15 15'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
            />
        </svg>
    );

    const enhanceDatePicker = (picker: any, key: any) => {
        if (!picker) return null;
        return React.cloneElement(picker, {
            key,
            label: '',
            slotProps: {
                textField: {
                    label: '',
                    variant: 'outlined',
                    size: 'small',
                    fullWidth: true,
                    InputLabelProps: { shrink: false },
                    sx: {
                        '& .MuiOutlinedInput-root': {
                            height: 44,
                            padding: 0,
                            borderRadius: 0,
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                        },
                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            border: '0 !important',
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                            border: '0 !important',
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: '0 !important',
                        },
                        '& .MuiOutlinedInput-input': {
                            textAlign: 'center',
                            fontWeight: 600,
                            color: '#6B7280',
                            padding: 0,
                        },
                        '& .MuiInputAdornment-root svg': {
                            color: '#9CA3AF',
                        },
                    },
                },
            },
        });
    };

    const startPickerStyled = enhanceDatePicker(startPicker, 'filter-start');
    const endPickerStyled = enhanceDatePicker(endPicker, 'filter-end');
    const showStatus = typeof onStatusChange === 'function' && statusFilter !== undefined;
    const showDeviceType = typeof onDeviceTypeChange === 'function' && deviceTypeFilter !== undefined;
    const showDuration = typeof onDurationChange === 'function' && durationRange !== undefined;
    const showLabel = typeof onLabelChange === 'function';

    const [selectedDeviceType, setSelectedDeviceType] = React.useState(deviceTypeFilter || 'all');
    const defaultDurationRange = React.useMemo(() => durationRange || [0, Number.POSITIVE_INFINITY], [durationRange]);
    const [selectedDuration, setSelectedDuration] = React.useState(defaultDurationRange);
    const [labels, setLabels] = React.useState([]);
    const [labelsLoaded, setLabelsLoaded] = React.useState(false);
    const [selectedLabels, setSelectedLabels] = React.useState(labelFilter || []);

    React.useEffect(() => {
        if (!showDeviceType) return;
        setSelectedDeviceType(deviceTypeFilter || 'all');
    }, [deviceTypeFilter, showDeviceType]);

    React.useEffect(() => {
        if (!showDuration) return;
        setSelectedDuration(durationRange || [0, Number.POSITIVE_INFINITY]);
    }, [durationRange, showDuration]);

    React.useEffect(() => {
        if (!showLabel) return;
        setSelectedLabels(labelFilter || []);
    }, [labelFilter, showLabel]);

    // The backdrop is decorative and hidden from assistive tech, so Escape is the
    // only way a keyboard user can dismiss the dialog without tabbing to the X.
    React.useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    React.useEffect(() => {
        if (!open || labelsLoaded || !showLabel) {
            return;
        }
        let mounted = true;
        getLabels()
            .then((res: any) => {
                if (!mounted) return;
                const normalized = (res?.labels || [])
                    .map((label: any) => ({
                        id: label?.id || label?.label_id || label?.labelId,
                        name: label?.name || label?.label_name || label?.labelName || label?.label,
                        color: label?.color || label?.label_color || label?.labelColor,
                    }))
                    .filter((label: any) => label.id);
                setLabels(normalized);
                setLabelsLoaded(true);
            })
            .catch(() => {
                if (!mounted) return;
                setLabels([]);
                setLabelsLoaded(true);
            });
        return () => {
            mounted = false;
        };
    }, [open, labelsLoaded, showLabel]);
    const devices = [
        { id: 'cotag', name: 'Cotag', image: '/GCC_shell_dev_sticker_without_bg-20.png' },
        { id: 'cotagLite', name: 'Cotag Lite', image: '/GCC_shell_dev_sticker_without_bg-21.png' },
        { id: 'cotagReuse', name: 'Cotag Re-use', image: '/GCC_shell_dev_sticker_without_bg-22.png' },
    ];

    const handleDeviceSelect = (value: any) => {
        setSelectedDeviceType(value);
        onDeviceTypeChange?.(value);
    };

    const handleDurationSelect = (value: any) => {
        setSelectedDuration(value);
        onDurationChange?.(value);
    };

    const handleLabelToggle = (labelId: any) => {
        const next = selectedLabels.includes(labelId)
            ? selectedLabels.filter((id: any) => id !== labelId)
            : [...selectedLabels, labelId];
        setSelectedLabels(next);
        onLabelChange?.(next);
    };

    const handleReset = () => {
        if (showDeviceType) {
            handleDeviceSelect('all');
        }
        if (showStatus) {
            onStatusChange?.('any');
        }
        if (showDuration) {
            handleDurationSelect(defaultDurationRange);
        }
        if (showLabel) {
            setSelectedLabels([]);
            onLabelChange?.([]);
        }
        onReset?.();
    };

    if (!open) return null;

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center' role='dialog' aria-modal='true'>
            <div aria-hidden='true' className='absolute inset-0 bg-white/80' onClick={onClose} />
            <div className='relative z-10 flex h-[656px] w-[480px] max-w-[90vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl'>
                <div className='relative border-b border-slate-200 px-6 py-4 text-center !text-[24px] font-bold leading-none text-[#53545C] font-[Roboto]'>
                    {filter}
                    <button
                        type='button'
                        onClick={onClose}
                        aria-label='Close filter dialog'
                        className='absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-2 text-slate-500 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-100'
                    >
                        <CloseIcon />
                    </button>
                </div>

                <div className='flex flex-1 flex-col gap-2 overflow-y-auto px-6 py-5'>
                    <Accordion type='multiple' className='w-full'>
                        <AccordionItem value='upload' className='border-b border-slate-200'>
                            <AccordionTrigger className='px-0 py-3 text-[20px] font-medium leading-none text-[#595A5C] hover:bg-transparent h-[72px] font-[Roboto]'>
                                {uploadTime}
                            </AccordionTrigger>
                            <AccordionContent className='pb-4'>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            overflow: 'hidden',
                                            borderRadius: '999px',
                                            bgcolor: 'white',
                                            border: '1px solid #DFE1E6',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: 'none !important',
                                            },
                                        }}
                                    >
                                        <div className='min-w-0 flex-1 px-3'>{startPickerStyled}</div>
                                        <div className='h-9 w-px self-center bg-slate-200' />
                                        <div className='min-w-0 flex-1 px-3'>{endPickerStyled}</div>
                                    </Box>
                                </LocalizationProvider>
                            </AccordionContent>
                        </AccordionItem>

                        {showStatus && (
                            <AccordionItem value='status' className='border-b border-slate-200'>
                                <AccordionTrigger className='px-0 py-3 text-[20px] font-medium leading-none text-[#595A5C] hover:bg-transparent h-[72px] font-[Roboto]'>
                                    {status}
                                </AccordionTrigger>
                                <AccordionContent className='pb-4'>
                                    <div className='flex gap-3 rounded-full border border-slate-200 bg-white p-1'>
                                        {[
                                            { value: 'any', label: anyStatus },
                                            { value: 'pass', label: pass },
                                            { value: 'fail', label: fail },
                                        ].map(({ value, label: optionLabel }: any) => {
                                            const isActive = statusFilter === value;
                                            return (
                                                <button
                                                    key={value}
                                                    type='button'
                                                    onClick={() => onStatusChange(value)}
                                                    className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none ${
                                                        isActive
                                                            ? 'bg-[#3F8EE8] text-white shadow-sm'
                                                            : 'text-slate-600 hover:bg-slate-50'
                                                    }`}
                                                >
                                                    {optionLabel}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        )}

                        {[
                            ...(showDeviceType ? [{ id: 'deviceType', title: deviceType }] : []),
                            ...(showLabel ? [{ id: 'label', title: label }] : []),
                            ...(showDuration ? [{ id: 'duration', title: duration }] : []),
                        ].map(({ id, title }: any) => (
                            <AccordionItem key={id} value={id} className='border-b border-slate-200 last:border-b-0'>
                                <AccordionTrigger className='px-0 py-3 text-[20px] font-medium leading-none text-[#595A5C] hover:bg-transparent h-[72px] font-[Roboto]'>
                                    {title}
                                </AccordionTrigger>
                                <AccordionContent className='pb-4 text-sm text-slate-500'>
                                    {id === 'deviceType' ? (
                                        <div className='flex flex-col gap-3'>
                                            <div className='grid w-full grid-cols-1 gap-4 sm:grid-cols-3'>
                                                {devices.map(({ id: deviceId, name, image }: any) => {
                                                    const isActive = selectedDeviceType === deviceId;
                                                    return (
                                                        <DeviceStickerButton
                                                            key={deviceId}
                                                            name={name}
                                                            image={image}
                                                            isActive={isActive}
                                                            onSelect={() => handleDeviceSelect(deviceId)}
                                                        />
                                                    );
                                                })}
                                            </div>
                                            <button
                                                type='button'
                                                onClick={() => handleDeviceSelect('all')}
                                                className={`w-full rounded-[30px] border border-[#DFE1E6] px-4 py-3 text-center text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-100 ${
                                                    selectedDeviceType === 'all'
                                                        ? 'bg-[#3F8EE8] text-white'
                                                        : 'bg-white text-slate-700 hover:bg-slate-50'
                                                }`}
                                            >
                                                {allDevice}
                                            </button>
                                        </div>
                                    ) : id === 'label' ? (
                                        <div className='flex flex-wrap gap-3'>
                                            {labels.length === 0 ? (
                                                <div className='text-sm text-slate-500'>No labels</div>
                                            ) : (
                                                labels.map((labelItem: any) => {
                                                    const isActive = selectedLabels.includes(labelItem.id);
                                                    const bgColor = labelItem.color || '#E5E7EB';
                                                    const textColor = '#111827';
                                                    return (
                                                        <button
                                                            key={labelItem.id}
                                                            type='button'
                                                            onClick={() => handleLabelToggle(labelItem.id)}
                                                            className='flex items-center gap-2 rounded-full border-4 px-3 py-2 text-xs font-semibold shadow-sm transition'
                                                            style={{
                                                                backgroundColor: bgColor,
                                                                color: textColor,
                                                                borderColor: isActive
                                                                    ? 'var(--primary)'
                                                                    : 'transparent',
                                                            }}
                                                        >
                                                            <span>{labelItem.name || labelItem.id}</span>
                                                        </button>
                                                    );
                                                })
                                            )}
                                        </div>
                                    ) : id === 'duration' ? (
                                        <DurationSlider
                                            value={selectedDuration}
                                            onChange={handleDurationSelect}
                                            tKey={tKey}
                                        />
                                    ) : (
                                        'No options available.'
                                    )}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>

                <div className='flex items-center justify-between border-t border-slate-200 px-6 py-3'>
                    <button
                        type='button'
                        onClick={handleReset}
                        className='text-sm font-semibold text-slate-600 transition hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100'
                    >
                        {resetFilter}
                    </button>
                    <button
                        type='button'
                        onClick={onApply}
                        className='rounded-full bg-blue-500 px-5 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200'
                    >
                        {apply}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default FilterDialog;
